/**
 * Wishlist Management Helper with API Integration
 * UPDATED for ProductDTO structure
 */

import { apiClient } from "../api/ApiClient";

// Check if user is logged in
export const isUserLoggedIn = () => {
  const token = localStorage.getItem('token');
  return !!token;
};


// ========== LOCAL STORAGE FUNCTIONS (for non-logged in users) ==========

// Get wishlist from localStorage
export const getLocalWishlist = () => {
  try {
    const wishlist = localStorage.getItem('wishlist');
    return wishlist ? JSON.parse(wishlist) : [];
  } catch (error) {
    console.error('Error getting local wishlist:', error);
    return [];
  }
};

// Add product variant to local wishlist
export const addToLocalWishlist = (productVariantId) => {
  try {
    const wishlist = getLocalWishlist();
    
    // Check if variant already exists
    const exists = wishlist.some(item => item.productVariantId === productVariantId);
    
    if (!exists) {
      wishlist.push({
        productVariantId,
        addedAt: new Date().toISOString()
      });
      
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      
      // Dispatch event to update UI
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      return { success: true, message: 'Product added to wishlist' };
    } else {
      return { success: false, message: 'Product already in wishlist' };
    }
  } catch (error) {
    console.error('Error adding to local wishlist:', error);
    return { success: false, message: 'Error adding to wishlist' };
  }
};

// Remove product from local wishlist
export const removeFromLocalWishlist = (productVariantId) => {
  try {
    const wishlist = getLocalWishlist();
    const updatedWishlist = wishlist.filter(item => item.productVariantId !== productVariantId);
    
    localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
    
    // Dispatch event to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return { success: true, message: 'Product removed from wishlist' };
  } catch (error) {
    console.error('Error removing from local wishlist:', error);
    return { success: false, message: 'Error removing from wishlist' };
  }
};

// Clear local wishlist
export const clearLocalWishlist = () => {
  try {
    localStorage.setItem('wishlist', JSON.stringify([]));
    
    // Dispatch event to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return { success: true, message: 'Wishlist cleared' };
  } catch (error) {
    console.error('Error clearing local wishlist:', error);
    return { success: false, message: 'Error clearing wishlist' };
  }
};

// Get local wishlist count
export const getLocalWishlistCount = () => {
  const wishlist = getLocalWishlist();
  return wishlist.length;
};

// Check if variant is in local wishlist
export const isInLocalWishlist = (productVariantId) => {
  const wishlist = getLocalWishlist();
  return wishlist.some(item => item.productVariantId === productVariantId);
};

// ========== API FUNCTIONS (for logged in users) ==========

// Get wishlist from API
export const getWishlist = async () => {
  try {
    const response = await apiClient.get(`/api/wishlist`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error getting wishlist:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error getting wishlist'
    };
  }
};

// Add to wishlist via API
export const addToWishlist = async (productVariantId) => {
  try {
    const response = await apiClient.post(
      `/api/wishlist/add`,
      { "productVariantId": Number(productVariantId) }
    );
    
    // Dispatch event to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return {
      success: true,
      data: response.data,
      message: 'Product added to wishlist'
    };
  } catch (error) {
    console.error('Error adding to wishlist:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error adding to wishlist'
    };
  }
};

// Remove from wishlist via API
export const removeFromWishlist = async (itemId) => {
  try {
    const response = await apiClient.delete(`/api/wishlist/items/${itemId}`);
    
    // Dispatch event to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return {
      success: true,
      data: response.data,
      message: 'Product removed from wishlist'
    };
  } catch (error) {
    if(error?.response?.data?.message === "Wishlist item not found"){
      window.dispatchEvent(new Event('wishlistUpdated'));
      return{
        success: true,
        message: 'Product removed from wishlist'
      };
    }
    console.error('Error removing from wishlist:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error removing from wishlist'
    };
  }
};

// Clear wishlist via API
export const clearWishlist = async () => {
  try {
    await apiClient.delete(`/api/wishlist/clear`);
    
    // Dispatch event to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return {
      success: true,
      message: 'Wishlist cleared'
    };
  } catch (error) {
    console.error('Error clearing wishlist:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error clearing wishlist'
    };
  }
};

// Get wishlist count via API
export const getWishlistCount = async () => {
  try {
    const response = await apiClient.get(`/api/wishlist/count`);
    return {
      success: true,
      count: response.data
    };
  } catch (error) {
    console.error('Error getting wishlist count:', error);
    return {
      success: false,
      count: 0
    };
  }
};

// Move item to cart via API
export const moveItemToCart = async (itemId) => {
  try {
    const response = await apiClient.post(`/api/wishlist/items/${itemId}/move-to-cart`);
    
    // Dispatch events to update UI
    window.dispatchEvent(new Event('wishlistUpdated'));
    window.dispatchEvent(new Event('cartUpdated'));
    
    return {
      success: true,
      data: response.data,
      message: 'Item moved to cart'
    };
  } catch (error) {
    console.error('Error moving item to cart:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error moving item to cart'
    };
  }
};

// ========== UNIFIED FUNCTIONS (auto-detect logged in/out) ==========

// Unified add to wishlist
export const addProductToWishlist = async (productVariantId) => {
  if (isUserLoggedIn()) {
    return await addToWishlist(productVariantId);
  } else {
    return addToLocalWishlist(productVariantId);
  }
};

// Unified remove from wishlist
export const removeProductFromWishlist = async (itemIdOrVariantId) => {
  if (isUserLoggedIn()) {
    return await removeFromWishlist(itemIdOrVariantId);
  } else {
    return removeFromLocalWishlist(itemIdOrVariantId);
  }
};

// Unified clear wishlist
export const clearAllWishlist = async () => {
  if (isUserLoggedIn()) {
    return await clearWishlist();
  } else {
    return clearLocalWishlist();
  }
};

// Unified get wishlist count
export const getAllWishlistCount = async () => {
  if (isUserLoggedIn()) {
    const result = await getWishlistCount();
    return result.count;
  } else {
    return getLocalWishlistCount();
  }
};

// Check if product is in wishlist (works for both logged in/out)
// UPDATED: Changed from item.variant.id to item.product.variants
export const isInWishlist = async (productVariantId) => {
  if (isUserLoggedIn()) {
    const result = await getWishlist();
    if (result.success && result.data) {
      // Check if any product has a variant with this ID
      return result.data.items.some(item => 
        item.product.variants && 
        item.product.variants.some(variant => variant.id === productVariantId)
      );
    }
    return false;
  } else {
    return isInLocalWishlist(productVariantId);
  }
};

// Toggle wishlist (add/remove)
// UPDATED: Changed to work with ProductDTO structure
export const toggleWishlist = async (productVariantId) => {
  const inWishlist = await isInWishlist(productVariantId);
  
  if (inWishlist) {
    if (isUserLoggedIn()) {
      // Need to find the item ID first
      const wishlistResult = await getWishlist();
      if (wishlistResult.success) {
        // Find the wishlist item that contains this variant
        const item = wishlistResult.data.items.find(i => 
          i.product.variants && 
          i.product.variants.some(variant => variant.id === productVariantId)
        );
        if (item) {
          return await removeFromWishlist(item.id);
        }
      }
    } else {
      return removeFromLocalWishlist(productVariantId);
    }
  } else {
    return await addProductToWishlist(productVariantId);
  }
};

// Move local wishlist item to cart (for non-logged in users)
export const moveLocalItemToCart = (item) => {
  try {
    // Remove from wishlist
    removeFromLocalWishlist(item.id);
    
    // Dispatch events
    window.dispatchEvent(new Event('wishlistUpdated'));
    
    return {
      success: true,
      message: 'Item moved to cart'
    };
  } catch (error) {
    console.error('Error moving local item to cart:', error);
    return {
      success: false,
      message: 'Error moving item to cart'
    };
  }
};

// Sync local wishlist with server after login
export const syncWishlistAfterLogin = async () => {
  try {
    const localWishlist = getLocalWishlist();
    
    if (localWishlist.length > 0) {
      // Add each item to server wishlist
      for (const item of localWishlist) {
        await addToWishlist(item.productVariantId);
      }
      
      // Clear local wishlist after sync
      clearLocalWishlist();
      
      return {
        success: true,
        message: 'Wishlist synced successfully'
      };
    }
    
    return {
      success: true,
      message: 'No items to sync'
    };
  } catch (error) {
    console.error('Error syncing wishlist:', error);
    return {
      success: false,
      message: 'Error syncing wishlist'
    };
  }
};